import { createContext, useEffect, useState } from 'react'

import { CssBaseline } from '@twistopayments/design-system/components/atoms/css-baseline'
import { SvgGradient } from '@twistopayments/design-system/components/atoms/svg-gradient'
import { ThemeProvider } from '@twistopayments/design-system/components/atoms/theme-provider'
import { ToastProvider } from '@twistopayments/design-system/components/atoms/toast'
import { Version } from '@twistopayments/design-system/components/atoms/version'
import {
  ErrorBoundary,
  ErrorModalProvider,
} from '@twistopayments/design-system/components/molecules/error-boundary'
import { isEmpty } from 'ramda'

import { AppConfigProvider, useSetupConfig } from '@twisto/base/app-config'
import { Location } from '@twisto/components/atoms/location'
import { useConfigEditor } from '@twisto/components/organisms/config-editor'
import { analytics } from '@twisto/utils'

type Props = {
  children: React.ReactNode
  /** region resolved from the request host during SSR */
  region?: GLOBALS.Region
}
// Context
type BaseAppContextType = { openConfigEditor: (() => void) | undefined }
export const BaseAppContext = createContext<BaseAppContextType>({
  openConfigEditor: undefined,
})

export const HomepageApp = ({ children, region }: Props) => {
  // The request region is only known on the first (server) render. Keep it for
  // client-side navigations, where `getInitialProps` runs without a request.
  const [initialRegion] = useState(region)
  const { openConfigEditor, ConfigEditor, configEditorOverride } =
    useConfigEditor()
  const config = useSetupConfig({
    configEditorOverride,
    region: initialRegion,
  })

  // persist query string to gtm object on mount
  useEffect(() => {
    if (!isEmpty(window.location.search)) {
      analytics.setGlobalData({ query: window.location.search })
    }
  }, [])

  const handleRouteChange = () => {
    // consumed by google tag manager
    const routeChangeEvent = new CustomEvent('routeChange')
    document.dispatchEvent(routeChangeEvent)

    return null
  }

  return (
    <ThemeProvider brand="b2c-legacy">
      <CssBaseline fontScaling={config.featureFlags.fontScaling} />
      <SvgGradient />
      <AppConfigProvider config={config}>
        <BaseAppContext.Provider value={{ openConfigEditor }}>
          <ErrorModalProvider>
            <Location>{handleRouteChange}</Location>
            <ErrorBoundary>
              <ToastProvider>
                {ConfigEditor && <ConfigEditor />}
                {children}
              </ToastProvider>
            </ErrorBoundary>
          </ErrorModalProvider>
        </BaseAppContext.Provider>
      </AppConfigProvider>
      <Version onClick={openConfigEditor} />
    </ThemeProvider>
  )
}
